import omit from 'lodash/omit'

import client, { Schemas } from '~/clients/api-client'
import { useStore } from '~/store/store'
import { type HospitalConfigLocation } from '~/storm'
import { selectDepartmentLocationAssignment } from '~/storm'
import { trackUserEvent } from '~/utils/analytics'

import type { ClientResponse } from '../../Form/formErrors'
import { getListDifferencesBy, hasDifference } from '../../Form/utils'
import type { LocationFormDefaultValues, LocationFormParsedValues, ProfileApiPayload } from './formState'
import { formServicePeriodsToApiServicePeriods, formStateToApiPayloads } from './formState'

const hasSaveError = (results: ClientResponse[]) => results.some(r => Boolean(r.error))

async function createBatchLocationProfiles(profiles: ProfileApiPayload[]): Promise<ClientResponse> {
    const profileToSurgeryTypeGroups = new Map<string, ProfileApiPayload['profileSurgeryTypeGroups']>()
    const profilesToCreate = profiles.map(p => {
        const uuid = crypto.randomUUID()
        profileToSurgeryTypeGroups.set(uuid, p.profileSurgeryTypeGroups)
        return {
            active_from: p.active_from,
            location_id: p.location_id,
            extra_staffing_config: p.extra_staffing_config,
            uuid,
        }
    })

    const res = await client.createBatchLocationProfile(profilesToCreate)
    const createdProfiles = res.data?.filter(Boolean)

    if (!createdProfiles) {
        console.error('Failed to create location profiles', { cause: res.error })
        return res
    }

    if (createdProfiles.length !== profilesToCreate.length) {
        const failedProfiles = profilesToCreate.filter(p => !createdProfiles.some(cp => cp.uuid === p.uuid))
        console.error('Failed to create some location profiles:', { cause: { data: res.data, error: res.error }, failedProfiles })
    }

    const surgeryTypeGroupsToCreate = createdProfiles.flatMap(p => {
        const groups = profileToSurgeryTypeGroups.get(p.uuid)
        if (!groups) return []
        return groups.map(s => ({
            location_profile_id: p.id,
            surgery_type_group_id: s.surgery_type_group_id,
        }))
    })

    if (surgeryTypeGroupsToCreate.length > 0) {
        return client.createBatchLocationProfileSurgeryTypeGroup(surgeryTypeGroupsToCreate)
    }
    return res
}

export async function saveEditLocation(
    values: LocationFormParsedValues,
    initialValues: LocationFormDefaultValues,
    originalLocation: HospitalConfigLocation
): Promise<ClientResponse[]> {
    const state = useStore.getState()
    const locationId = originalLocation.id
    const payloads = formStateToApiPayloads(values, locationId)
    const initialPayloads = formStateToApiPayloads(initialValues, locationId)

    const promises: Promise<ClientResponse>[] = []

    if (hasDifference(payloads.location, initialPayloads.location)) {
        const shouldUpdateServicePeriods = hasDifference(payloads.location.servicePeriods, initialPayloads.location.servicePeriods)
        promises.push(
            client.partialUpdateLocation(locationId, {
                room_code: values.roomCode,
                default_care_level: values.defaultCareLevel,
                service_periods: shouldUpdateServicePeriods ? payloads.location.servicePeriods : undefined,
            })
        )
    }

    const profilesDiff = getListDifferencesBy(payloads.locationProfiles, initialPayloads.locationProfiles, p => p.id)

    if (profilesDiff.added.length > 0) {
        promises.push(createBatchLocationProfiles(profilesDiff.added))
    }
    if (profilesDiff.removed.length > 0) {
        promises.push(client.deleteBatchLocationProfiles(profilesDiff.removed.map(p => p.id).filter(Boolean)))
    }

    const batchUpdates = {
        locationProfileUpdates: [] as Schemas['BaseEntitySchemaUpdate_LocationProfileCreate_'][],
        surgeryTypeGroupCreates: [] as Schemas['LocationProfileSurgeryTypeGroupCreate'][],
        surgeryTypeGroupUpdates: [] as Schemas['BaseEntitySchemaUpdate_LocationProfileSurgeryTypeGroupCreate_'][],
        surgeryTypeGroupDeletes: [] as number[],
    }

    profilesDiff.updated.forEach(({ new: newProfile, initial: initialProfile }) => {
        const profileId = initialProfile.id
        if (!profileId) return

        const primitiveNewProfile = omit(newProfile, 'profileSurgeryTypeGroups')
        const primitiveInitialProfile = omit(initialProfile, 'profileSurgeryTypeGroups')
        const hasProfileDiff = hasDifference(primitiveNewProfile, primitiveInitialProfile)

        if (hasProfileDiff) {
            batchUpdates.locationProfileUpdates.push({
                id: profileId,
                body: primitiveNewProfile,
            })
        }

        const initialGroups = initialProfile.profileSurgeryTypeGroups.map(s => ({ ...s, location_profile_id: profileId }))
        const newGroups = newProfile.profileSurgeryTypeGroups.map(s => ({ ...s, location_profile_id: profileId }))

        const groupDiffs = getListDifferencesBy(newGroups, initialGroups, s => s.surgery_type_group_id)

        if (groupDiffs.added.length > 0) {
            batchUpdates.surgeryTypeGroupCreates.push(...groupDiffs.added)
        }
        if (groupDiffs.removed.length > 0) {
            batchUpdates.surgeryTypeGroupDeletes.push(...groupDiffs.removed.map(s => s.id).filter(Boolean))
        }
        if (groupDiffs.updated.length > 0) {
            batchUpdates.surgeryTypeGroupUpdates.push(
                ...groupDiffs.updated.map(s => ({
                    id: s.initial.id,
                    body: { ...s.new, location_profile_id: profileId },
                }))
            )
        }
    })

    if (batchUpdates.locationProfileUpdates.length > 0) {
        promises.push(client.updateBatchLocationProfiles(batchUpdates.locationProfileUpdates))
    }
    if (batchUpdates.surgeryTypeGroupCreates.length > 0) {
        promises.push(client.createBatchLocationProfileSurgeryTypeGroup(batchUpdates.surgeryTypeGroupCreates))
    }
    if (batchUpdates.surgeryTypeGroupDeletes.length > 0) {
        promises.push(client.deleteBatchLocationProfileSurgeryTypeGroups(batchUpdates.surgeryTypeGroupDeletes))
    }
    if (batchUpdates.surgeryTypeGroupUpdates.length > 0) {
        promises.push(client.updateBatchLocationProfileSurgeryTypeGroups(batchUpdates.surgeryTypeGroupUpdates))
    }

    // Department assignments
    const departmentsDiff = getListDifferencesBy(payloads.departmentAssignments, initialPayloads.departmentAssignments, da => da.department_id)
    if (departmentsDiff.added.length > 0) {
        promises.push(client.createBatchDepartmentLocationAssignment(departmentsDiff.added))
    }
    if (departmentsDiff.removed.length > 0) {
        const departmentAssignmentIds = departmentsDiff.removed
            .map(da => selectDepartmentLocationAssignment(state, { where: { department_id: da.department_id, location_id: locationId } })?.id)
            .filter(Boolean)
        promises.push(client.deleteBatchDepartmentLocationAssignments(departmentAssignmentIds))
    }

    const results = await Promise.all(promises)
    if (!hasSaveError(results)) {
        void trackUserEvent('HOSPITAL_CONFIGURATION_ROOM_EDITED', {
            roomCode: values.roomCode,
            profileCount: payloads.locationProfiles.length,
            hasChanges: promises.length > 0,
        })
    }
    return results
}

export async function saveNewLocation(values: LocationFormParsedValues): Promise<ClientResponse[]> {
    const promises: Promise<ClientResponse>[] = []

    const apiServicePeriods = formServicePeriodsToApiServicePeriods(values.servicePeriods)
    const locationPromise = client.createLocation({
        room_code: values.roomCode,
        default_care_level: values.defaultCareLevel,
        service_periods: apiServicePeriods,
    })
    promises.push(locationPromise)
    const locationResult = await locationPromise
    if (locationResult.error) {
        return [locationResult]
    }

    const locationId = locationResult.data.id
    const payloads = formStateToApiPayloads(values, locationId)

    if (payloads.departmentAssignments.length > 0) {
        promises.push(client.createBatchDepartmentLocationAssignment(payloads.departmentAssignments))
    }
    if (payloads.locationProfiles.length > 0) {
        promises.push(createBatchLocationProfiles(payloads.locationProfiles))
    }

    const results = await Promise.all(promises)
    if (!hasSaveError(results)) {
        void trackUserEvent('HOSPITAL_CONFIGURATION_ROOM_CREATED', {
            roomCode: values.roomCode,
            profileCount: payloads.locationProfiles.length,
        })
    }
    return results
}
